import { HorizontalContainer, VerticalContainer, useFormField } from '@american-autonomy/ag-ui';

import IntervalUnitToggle from 'components/IntervalUnitToggle';
import LinkButton from 'components/LinkButton';
import StepperInput from 'components/inputs/StepperInput';
import BodyText from 'components/text/BodyText';
import CaptionText from 'components/text/CaptionText';

import useTranslation from './hooks/useTranslation';

import {
    MAX_INTERVAL_VALUE,
    MIN_INTERVAL_VALUE,
    RESET_BUTTON_TEST_ID,
    STEPPER_TEST_ID,
    TEST_ID
} from './constants';
import type { FieldValues, Props } from './types';

import styles from './styles.module.scss';

const ServiceIntervalField = <T extends FieldValues>(props: Props<T>) => {
    const {
        className = '',
        control,
        defaultIntervalUnit,
        defaultIntervalValue,
        isDisabled = false,
        label,
        oemLabel,
        resetLabel,
        testId = TEST_ID,
        unitLabel,
        unitName,
        units,
        valueName
    } = props;

    const { INTERVAL_OUT_OF_RANGE_ERROR, INTERVAL_REQUIRED_ERROR } = useTranslation();

    const outOfRangeError = INTERVAL_OUT_OF_RANGE_ERROR({
        max: MAX_INTERVAL_VALUE,
        min: MIN_INTERVAL_VALUE
    });

    const {
        field: { onChange: onValueChange, value }
    } = useFormField({
        control,
        name: valueName,
        rules: {
            max: { message: outOfRangeError, value: MAX_INTERVAL_VALUE },
            min: { message: outOfRangeError, value: MIN_INTERVAL_VALUE },
            required: INTERVAL_REQUIRED_ERROR
        }
    });

    const {
        field: { onChange: onUnitChange, value: unit }
    } = useFormField({ control, name: unitName });

    const handleReset = () => {
        onValueChange(defaultIntervalValue);
        onUnitChange(defaultIntervalUnit);
    };

    const isAtDefault = value === defaultIntervalValue && unit === defaultIntervalUnit;
    const canReset = !isDisabled && !isAtDefault;

    return (
        <HorizontalContainer
            className={`${className} ${styles.serviceIntervalField}`}
            testId={testId}>
            <BodyText className={styles.partLabel} text={label} />

            <StepperInput
                className={styles.stepper}
                isDisabled={isDisabled}
                max={MAX_INTERVAL_VALUE}
                min={MIN_INTERVAL_VALUE}
                name={valueName as string}
                onChange={onValueChange}
                testId={STEPPER_TEST_ID}
                value={value}
            />

            {units.length > 1 ? (
                <IntervalUnitToggle
                    isDisabled={isDisabled}
                    onSelect={onUnitChange}
                    selectedUnit={unit}
                    units={units}
                />
            ) : (
                <CaptionText className={styles.unitLabel} text={unitLabel} />
            )}

            <VerticalContainer className={styles.defaultColumn}>
                <CaptionText className={styles.oemLabel} text={oemLabel} />

                {canReset && (
                    <LinkButton
                        className={styles.resetButton}
                        onClick={handleReset}
                        testId={RESET_BUTTON_TEST_ID}
                        text={resetLabel}
                    />
                )}
            </VerticalContainer>
        </HorizontalContainer>
    );
};

export default ServiceIntervalField;
